import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import FilterChips from '../../../common/filter-chips/filter-chips';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import CriticalOrderHowItWorks from '../../../common/critical-order-how-it-works/critical-order-how-it-works';
import axios from 'axios';
import React, { useContext, useState, useEffect } from 'react';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { useCriticalOrders } from '../../../../hooks/use-critical-orders';

interface CriticalOrderCreditSectionProps {
  merchant: MerchantType;
  onUpdate?: (updatedMerchant: MerchantType) => void;
  canEdit: boolean;
}

function CriticalOrderCreditSection({
  merchant,
  onUpdate,
  canEdit,
}: CriticalOrderCreditSectionProps) {
  const { config } = useContext(AuthContext);
  const { settings: orgSettings } = useContext(OrgSettingsContext);
  const { fetchBalance, loadingBalance, balance: criticalOrderBalance } = useCriticalOrders();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Local state for form
  const [useOrgCredit, setUseOrgCredit] = useState(merchant.critical_order_monthly_credit === null || merchant.critical_order_monthly_credit === undefined);
  const [monthlyCredit, setMonthlyCredit] = useState<string>(merchant.critical_order_monthly_credit?.toString() || '');
  const [useOrgMethod, setUseOrgMethod] = useState(merchant.critical_order_surcharge_method === null || merchant.critical_order_surcharge_method === undefined);
  const [surchargeMethod, setSurchargeMethod] = useState<'FIXED_SURCHARGE' | 'FIXED_FACTOR'>(
    merchant.critical_order_surcharge_method || 'FIXED_SURCHARGE'
  );
  const [fixedAmount, setFixedAmount] = useState<string>(
    merchant.critical_order_surcharge_fixed_amount && merchant.critical_order_surcharge_fixed_amount.length > 0
      ? String(merchant.critical_order_surcharge_fixed_amount[0][1])
      : ''
  );
  const [factor, setFactor] = useState<string>(
    merchant.critical_order_surcharge_factor || ''
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    monthlyCredit?: string;
    fixedAmount?: string;
    factor?: string;
  }>({});


  // Fetch critical order balance on mount
  useEffect(() => {
    if (merchant.id) {
      fetchBalance(merchant.id);
    }
  }, [merchant.id, fetchBalance]);

  // Track if values have changed
  useEffect(() => {
    const creditChanged = useOrgCredit ? 
      merchant.critical_order_monthly_credit !== null && merchant.critical_order_monthly_credit !== undefined : 
      merchant.critical_order_monthly_credit !== parseInt(monthlyCredit || '0');
    
    const methodChanged = useOrgMethod ? 
      merchant.critical_order_surcharge_method !== null && merchant.critical_order_surcharge_method !== undefined : 
      merchant.critical_order_surcharge_method !== surchargeMethod;

    const fixedAmountChanged = !useOrgMethod && surchargeMethod === 'FIXED_SURCHARGE' && (
      !merchant.critical_order_surcharge_fixed_amount || 
      merchant.critical_order_surcharge_fixed_amount.length === 0 ||
      String(merchant.critical_order_surcharge_fixed_amount[0][1]) !== fixedAmount
    );

    const factorChanged = !useOrgMethod && surchargeMethod === 'FIXED_FACTOR' && 
      merchant.critical_order_surcharge_factor !== factor;

    setHasChanges(creditChanged || methodChanged || fixedAmountChanged || factorChanged);
  }, [useOrgCredit, monthlyCredit, useOrgMethod, surchargeMethod, fixedAmount, factor, merchant]);

  const validateForm = () => {
    const errors: { monthlyCredit?: string; fixedAmount?: string; factor?: string } = {};

    // Validate monthly credit if not using org default
    if (!useOrgCredit) {
      if (!monthlyCredit || monthlyCredit.trim() === '') {
        errors.monthlyCredit = 'Monthly credit is required';
      } else {
        const creditNum = parseInt(monthlyCredit);
        if (isNaN(creditNum) || creditNum < 0) {
          errors.monthlyCredit = 'Monthly credit must be a non-negative number';
        }
      }
    }

    // Validate surcharge method specific fields if not using org default
    if (!useOrgMethod) {
      if (surchargeMethod === 'FIXED_SURCHARGE') {
        if (!fixedAmount || fixedAmount.trim() === '') {
          errors.fixedAmount = 'Fixed surcharge amount is required';
        } else {
          const amountNum = parseFloat(fixedAmount);
          if (isNaN(amountNum) || amountNum < 0) {
            errors.fixedAmount = 'Fixed surcharge amount must be a non-negative number';
          }
        }
      } else if (surchargeMethod === 'FIXED_FACTOR') {
        if (!factor || factor.trim() === '') {
          errors.factor = 'Multiplier factor is required';
        } else {
          const factorNum = parseFloat(factor);
          if (isNaN(factorNum) || factorNum < 1) {
            errors.factor = 'Multiplier factor must be a number greater than or equal to 1';
          }
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const payload: any = {
        critical_order_monthly_credit: useOrgCredit ? null : parseInt(monthlyCredit),
        critical_order_surcharge_method: useOrgMethod ? null : surchargeMethod,
      };

      if (!useOrgMethod) {
        if (surchargeMethod === 'FIXED_SURCHARGE') {
          payload.critical_order_surcharge_fixed_amount = [['USD', fixedAmount]];
        } else if (surchargeMethod === 'FIXED_FACTOR') {
          payload.critical_order_surcharge_factor = factor;
        }
      } else {
        // Clear merchant-specific values when using org defaults
        payload.critical_order_surcharge_fixed_amount = [];
        payload.critical_order_surcharge_factor = null;
      }

      const response = await axios.patch(
        `${API_ENDPOINT}/api/v1/merchant/${merchant.id}/`,
        payload,
        config
      );

      setSuccess(true);
      setHasChanges(false);
      setIsDialogOpen(false);
      setTimeout(() => setSuccess(false), 3000);
      
      // Refresh critical order balance
      if (merchant.id) {
        fetchBalance(merchant.id);
      }
      
      if (onUpdate && response.data) {
        onUpdate(response.data);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setError('Failed to update: ' + e.message);
      } else {
        setError('Failed to update merchant');
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get effective values for display
  const getEffectiveMonthlyCredit = () => {
    if (merchant.critical_order_monthly_credit !== null && merchant.critical_order_monthly_credit !== undefined) {
      return merchant.critical_order_monthly_credit;
    }
    return orgSettings.default_merchant_critical_order_monthly_credit || 0;
  };

  const getEffectiveSurchargeMethod = () => {
    if (merchant.critical_order_surcharge_method) {
      return merchant.critical_order_surcharge_method;
    }
    return orgSettings.critical_order_surcharge_method || 'Not configured';
  };

  const getEffectiveSurchargeValue = () => {
    const method = getEffectiveSurchargeMethod();
    if (method === 'FIXED_SURCHARGE') {
      const amount = merchant.critical_order_surcharge_fixed_amount && merchant.critical_order_surcharge_fixed_amount.length > 0 
        ? merchant.critical_order_surcharge_fixed_amount 
        : orgSettings.critical_order_surcharge_fixed_amount;
      return amount && amount.length > 0 ? `$${amount[0][1]}` : '$0';
    } else if (method === 'FIXED_FACTOR') {
      const factor = merchant.critical_order_surcharge_factor !== null 
        ? merchant.critical_order_surcharge_factor 
        : orgSettings.critical_order_surcharge_factor;
      return factor ? `${factor}x` : '1.0x';
    }
    return 'Not configured';
  };

  return (
    <>
      <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" gutterBottom>
            Critical Order Credits
          </Typography>
          {canEdit && (
            <IconButton 
              onClick={() => setIsDialogOpen(true)}
              size="small"
            >
              <Edit />
            </IconButton>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Monthly Limit and Surcharge side by side */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Monthly Limit
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getEffectiveMonthlyCredit()} orders
                {merchant.critical_order_monthly_credit !== null && merchant.critical_order_monthly_credit !== undefined 
                  ? ' (Custom)' 
                  : ' (Default)'}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Surcharge
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getEffectiveSurchargeMethod() === 'FIXED_SURCHARGE' ? `Fixed ${getEffectiveSurchargeValue()}` : 
                 getEffectiveSurchargeMethod() === 'FIXED_FACTOR' ? `Factor ${getEffectiveSurchargeValue()}` : 
                 'Not configured'}
                {merchant.critical_order_surcharge_method 
                  ? ' (Custom)' 
                  : ' (Default)'}
              </Typography>
            </Box>
          </Box>

          {/* Current Balance */}
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Current Balance
            </Typography>
            {loadingBalance ? (
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Loading...
              </Typography>
            ) : criticalOrderBalance ? (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {criticalOrderBalance.current_balance} / {criticalOrderBalance.effective_monthly_credit} used
                {criticalOrderBalance.remaining_credit > 0 ? (
                  <Typography component="span" sx={{ color: 'success.main', ml: 0.5 }}>
                    ({criticalOrderBalance.remaining_credit} left)
                  </Typography>
                ) : (
                  <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    (Over limit)
                  </Typography>
                )}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Unable to load
              </Typography>
            )}
          </Box>
        </Box>

        {/* Success/Error Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">Settings updated successfully!</Typography>
          </Alert>
        )}
      </Card>

      {/* Edit Dialog */}
      <CustomDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Critical Order Credit Settings
        </DialogTitle>
        
        <DialogContent>
            {/* Left side - Form inputs */}
            <Box mb={1}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Configure monthly critical order limits and surcharge settings for this merchant.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Monthly Credit */}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useOrgCredit}
                        onChange={(e) => setUseOrgCredit(e.target.checked)}
                      />
                    }
                    label="Use organisation default monthly credit"
                  />

                  {!useOrgCredit && (
                    <TextField
                      fullWidth
                      type="number"
                      label="Monthly Critical Order Credit"
                      value={monthlyCredit}
                      onChange={(e) => {
                        setMonthlyCredit(e.target.value);
                        if (validationErrors.monthlyCredit) {
                          setValidationErrors(prev => ({ ...prev, monthlyCredit: undefined }));
                        }
                      }}
                      error={!!validationErrors.monthlyCredit}
                      helperText={validationErrors.monthlyCredit || "Number of critical orders allowed per month before surcharge applies"}
                      sx={{ mt: 1 }}
                      inputProps={{ 
                        min: 0,
                        onWheel: (e: any) => e.target.blur() // Prevent wheel scroll
                      }}
                    />
                  )}

                  {useOrgCredit && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Using organisation default: {orgSettings.default_merchant_critical_order_monthly_credit || 0} credits per month
                    </Alert>
                  )}
                </Box>

                {/* Surcharge Method */}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useOrgMethod}
                        onChange={(e) => setUseOrgMethod(e.target.checked)}
                      />
                    }
                    label="Use organisation default surcharge method"
                  />

                  {!useOrgMethod && (
                    <>
                      <Box sx={{ mt: 2 }}>
                        <FilterChips
                          label="Surcharge Method"
                          options={[
                            { key: 'FIXED_SURCHARGE', label: 'Fixed Surcharge' },
                            { key: 'FIXED_FACTOR', label: 'Fixed Factor' }
                          ]}
                          selected={[{ key: surchargeMethod, label: surchargeMethod === 'FIXED_SURCHARGE' ? 'Fixed Surcharge' : 'Fixed Factor' }]}
                          onChange={(selected) => {
                            if (selected.length > 0) {
                              setSurchargeMethod(selected[0].key as 'FIXED_SURCHARGE' | 'FIXED_FACTOR');
                            }
                          }}
                          getOptionLabel={(option) => option.label}
                          optionsEqual={(a, b) => a.key === b.key}
                        />
                      </Box>

                      {surchargeMethod === 'FIXED_SURCHARGE' && (
                        <TextField
                          fullWidth
                          type="number"
                          label="Fixed Surcharge Amount (USD)"
                          value={fixedAmount}
                          onChange={(e) => {
                            setFixedAmount(e.target.value);
                            if (validationErrors.fixedAmount) {
                              setValidationErrors(prev => ({ ...prev, fixedAmount: undefined }));
                            }
                          }}
                          error={!!validationErrors.fixedAmount}
                          helperText={validationErrors.fixedAmount || "Extra charge added to delivery fee when merchant exceeds credit limit"}
                          sx={{ mt: 2 }}
                          inputProps={{ 
                            min: 0, 
                            step: '0.01',
                            onWheel: (e: any) => e.target.blur() // Prevent wheel scroll
                          }}
                        />
                      )}

                      {surchargeMethod === 'FIXED_FACTOR' && (
                        <TextField
                          fullWidth
                          type="number"
                          label="Multiplier Factor"
                          value={factor}
                          onChange={(e) => {
                            setFactor(e.target.value);
                            if (validationErrors.factor) {
                              setValidationErrors(prev => ({ ...prev, factor: undefined }));
                            }
                          }}
                          error={!!validationErrors.factor}
                          helperText={validationErrors.factor || "Multiplier for base delivery fee (e.g., 2.0 = doubles the delivery charge)"}
                          sx={{ mt: 2 }}
                          inputProps={{ 
                            min: 1, 
                            step: '0.1',
                            onWheel: (e: any) => e.target.blur() // Prevent wheel scroll
                          }}
                        />
                      )}
                    </>
                  )}

                  {useOrgMethod && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Using organisation default: {orgSettings.critical_order_surcharge_method || 'Not configured'}
                    </Alert>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right side - How it works explanation */}
           
              <CriticalOrderHowItWorks
                monthlyCredit={useOrgCredit ? (orgSettings.default_merchant_critical_order_monthly_credit || '0') : (monthlyCredit || '0')}
                surchargeMethod={useOrgMethod ? orgSettings.critical_order_surcharge_method : surchargeMethod}
                fixedAmount={useOrgMethod ? (orgSettings.critical_order_surcharge_fixed_amount?.[0]?.[1]?.toString() || '0') : (fixedAmount || '0')}
                factor={useOrgMethod ? (orgSettings.critical_order_surcharge_factor?.toString() || '1.0') : (factor || '1.0')}
              />

        </DialogContent>

        <DialogActions>
          <Button 
            onClick={() => setIsDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !hasChanges}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Settings'}
          </Button>
        </DialogActions>
      </CustomDialog>
    </>
  );
}

export default CriticalOrderCreditSection;

